// Shared content for the /method section and its onboarding tour.
// Single source of truth so the static pages and the guided tour never drift.
//
// Book-sourced text is pulled live from `data/glossary.ts` (already-vetted,
// already-cited paraphrase of Managing Priorities by Harry Max) rather than
// duplicated here — see getPhaseBookMeaning / getToolboxDefinition.

import { glossaryEntries } from '../../data/glossary';

export type DegapPhase = 'decide' | 'engage' | 'gather' | 'arrange' | 'prioritize';

export interface MethodLink {
  label: string;
  path: string;
}

export interface MethodPhaseContent {
  phase: DegapPhase;
  order: number;
  title: string;
  glossaryTerm: string;
  color: string;
  tint: string;
  inTheApp: string;
  primaryLink: MethodLink;
  secondaryLink: MethodLink;
  workbookPath: string;
}

// Same phase colors as the interactive workbook (src/lib/workbook/tokens.ts)
// so "explain it" (Method) and "do it" (Workbook) read as one visual system.
export const METHOD_PHASES: MethodPhaseContent[] = [
  {
    phase: 'decide',
    order: 1,
    title: 'Decide',
    glossaryTerm: 'Decide',
    color: '#0B6E4F',
    tint: '#E6F3EF',
    inTheApp:
      'In Priorities.ai, this is the moment a cycle gets created — a team or workspace formally committing to run a prioritization process instead of letting priorities happen by default.',
    primaryLink: { label: 'Start a cycle', path: '/prioritization/cycles/new' },
    secondaryLink: { label: 'See active cycles', path: '/prioritization/cycles' },
    workbookPath: '/workbook/decide',
  },
  {
    phase: 'engage',
    order: 2,
    title: 'Engage',
    glossaryTerm: 'Engage',
    color: '#155FA0',
    tint: '#E7F0FA',
    inTheApp:
      "This is what Catchball is for: tossing an early frame or objective to the stakeholders who need to weigh in, and building the back-and-forth that gets people genuinely bought in before anything gets ranked.",
    primaryLink: { label: 'Open Cascade', path: '/prioritization/cascade' },
    secondaryLink: { label: 'Check stakeholder pulse', path: '/prioritization/pulse' },
    workbookPath: '/workbook/engage',
  },
  {
    phase: 'gather',
    order: 3,
    title: 'Gather',
    glossaryTerm: 'Gather',
    color: '#6A1B9A',
    tint: '#F3E8F7',
    inTheApp:
      'This is item intake — every idea, request, and initiative gets captured as an item, so nothing worth considering is left out before the comparing starts.',
    primaryLink: { label: 'Submit an item', path: '/prioritization/intake/submit' },
    secondaryLink: { label: 'Browse the item pool', path: '/prioritization/items' },
    workbookPath: '/workbook/gather',
  },
  {
    phase: 'arrange',
    order: 4,
    title: 'Arrange',
    glossaryTerm: 'Arrange',
    color: '#D35400',
    tint: '#FCEFE6',
    inTheApp:
      'This is what a session does: taking everything gathered and putting it into a shape people can actually compare — clustered, related, and readable side by side.',
    primaryLink: { label: 'Run a session', path: '/prioritization/sessions' },
    secondaryLink: { label: 'See clusters & relationships', path: '/prioritization/relationships/clusters' },
    workbookPath: '/workbook/arrange',
  },
  {
    phase: 'prioritize',
    order: 5,
    title: 'Prioritize',
    glossaryTerm: 'Prioritize',
    color: '#B71C1C',
    tint: '#FCEBEC',
    inTheApp:
      'This is ratification and the Plan of Record: items get ranked with a real method instead of a gut call, and the result becomes the plan the team is actually accountable to.',
    primaryLink: { label: 'Ratify priorities', path: '/prioritization/governance/ratification' },
    secondaryLink: { label: 'View the Plan of Record', path: '/planning/plans' },
    workbookPath: '/workbook/prioritize',
  },
];

export function getMethodPhase(phase: string | undefined): MethodPhaseContent | undefined {
  return METHOD_PHASES.find((p) => p.phase === phase);
}

/** Faithful paraphrase of the book's meaning for a DEGAP phase, sourced from
 *  the existing glossary entry (already cited to Managing Priorities). Falls
 *  back to the entry's primary definition if no book-sourced alternate exists. */
export function getPhaseBookMeaning(phase: DegapPhase): string {
  const content = getMethodPhase(phase);
  if (!content) return '';
  const entry = glossaryEntries.find((e) => e.term === content.glossaryTerm);
  const bookAlt = entry?.alternateDefinitions?.find((a) => a.source === 'Managing Priorities');
  return bookAlt?.definition ?? entry?.definition ?? '';
}

export interface ObjectTypeContent {
  name: string;
  examples: string;
}

// The six universal object types from Managing Priorities, as already
// canonized in docs/1-canon/01-terminology-and-glossary.md ("Object Type").
export const OBJECT_TYPES: ObjectTypeContent[] = [
  { name: 'Activities', examples: 'projects, features, tasks, initiatives' },
  { name: 'People', examples: 'candidates, team members, stakeholders' },
  { name: 'Places', examples: 'markets, locations, geographies' },
  { name: 'Things', examples: 'assets, vendors, acquisitions, tools' },
  { name: 'Information', examples: 'signals, inputs, data sources, reports' },
  { name: 'Time', examples: 'periods, horizons, phases, schedules' },
];

export interface ToolboxEntry {
  /** Must match a term in data/glossary.ts exactly, for live definition lookup. */
  term: string;
  path: string;
  phase: DegapPhase;
}

// Named techniques from the book that already have a working tool in this
// app. Grouped by the DEGAP phase each is typically used in. Deliberately
// excludes anything individual/solo (Morning Boot Routine lives at
// /lab/morning-boot, not here) and anything without a confirmed glossary
// entry to link to (e.g. Speedboat/Sailboat has no page and no entry yet).
export const TOOLBOX_ENTRIES: ToolboxEntry[] = [
  { term: 'Buy a Feature', path: '/buy-a-feature', phase: 'gather' },
  { term: 'Prune the Product Tree', path: '/prune-product-tree', phase: 'gather' },
  { term: 'Impact-Effort Matrix', path: '/impact-effort-matrix', phase: 'arrange' },
  { term: 'Pareto Analysis', path: '/pareto-analysis', phase: 'arrange' },
  { term: 'RICE', path: '/rice-scoring', phase: 'prioritize' },
  { term: 'WSJF', path: '/wsjf', phase: 'prioritize' },
  { term: 'Eisenhower Matrix', path: '/eisenhower-matrix', phase: 'prioritize' },
  { term: 'Stack Ranking', path: '/stack-ranking', phase: 'prioritize' },
  { term: 'Simple Weighted Scoring', path: '/simple-weighted-scoring', phase: 'prioritize' },
  { term: 'Dot Voting', path: '/dot-voting', phase: 'prioritize' },
  { term: 'Pairwise Ranking', path: '/pairwise-ranking', phase: 'prioritize' },
  { term: 'MoSCoW Method', path: '/moscow-method', phase: 'prioritize' },
  { term: 'Max Priorities Pyramid', path: '/max-priorities-pyramid', phase: 'prioritize' },
  { term: 'Fast vs. Right Framework', path: '/fast-vs-right', phase: 'prioritize' },
  { term: 'Cost of Delay', path: '/cost-of-delay', phase: 'prioritize' },
];

export function getToolboxDefinition(term: string): string {
  return glossaryEntries.find((e) => e.term === term)?.definition ?? '';
}

// localStorage key the tour sets on finishing (the first-run teaser it once gated was removed 2026-11-03). Deliberately distinct from
// the dead onboarding components' keys (peak_priorities_onboarding_complete /
// peak-priorities-onboarding-complete) so this feature owns its own state.
export const METHOD_TEASER_DISMISSED_KEY = 'method_intro_dismissed';
